import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "xstyle", "as", "className", "style"];
import { c as _c } from "react-compiler-runtime";
import * as stylex from '@stylexjs/stylex';
import { renderSlotContent } from '../../utils/slot';

/**
 * Props when no wrapper element is used (as is not provided).
 * In this case, xstyle and additional props are not allowed.
 */

/**
 * Props when a wrapper element is used (as is provided).
 * In this case, xstyle and additional props can be provided.
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A flexible wrapper component that can render either an HTML element or a custom component
 * with StyleX styles applied. Automatically handles SlotContent and only renders the wrapper
 * if the content resolves to a non-empty value.
 *
 * If children is a render function that returns falsy,
 * this component will render nothing (no wrapper). This allows components to always render
 * SlotWrapper without checking if the content exists first.
 *
 * @example
 * // Render without wrapper (just the slot content)
 * <SlotWrapper>
 *   {content}
 * </SlotWrapper>
 *
 * @example
 * // Render as a styled div
 * <SlotWrapper as="div" xstyle={styles.container}>
 *   {content}
 * </SlotWrapper>
 *
 * @example
 * // Render as a different HTML tag
 * <SlotWrapper as="section" xstyle={styles.section}>
 *   {content}
 * </SlotWrapper>
 *
 * @example
 * // Render as a custom component with additional props
 * <SlotWrapper as={HStack} xstyle={styles.header} gap="sm">
 *   {content}
 * </SlotWrapper>
 *
 * @example
 * // When slot content needs arguments, use renderSlotContent to resolve before passing to SlotWrapper
 *   return (
 *     <SlotWrapper as={IconWrapper} xstyle={styles.icon}>
 *       {renderSlotContent(icon, isSelected)}
 *     </SlotWrapper>
 *   );
 * };
 */
export const SlotWrapper = t0 => {
  const $ = _c(19);
  let as;
  let children;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        xstyle: t2,
        as: t3
      } = t0,
      t4 = _objectWithoutPropertiesLoose(t0, _excluded);
    children = t1;
    xstyle = t2;
    as = t3;
    restProps = t4;
    $[0] = t0;
    $[1] = as;
    $[2] = children;
    $[3] = restProps;
    $[4] = xstyle;
  } else {
    as = $[1];
    children = $[2];
    restProps = $[3];
    xstyle = $[4];
  }
  let t1;
  if ($[5] !== children) {
    t1 = renderSlotContent(children);
    $[5] = children;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const resolvedContent = t1;
  if (!as) {
    return resolvedContent;
  }
  if (resolvedContent === null || resolvedContent === undefined || resolvedContent === "") {
    return resolvedContent;
  }
  const Component = as;
  const isElementTag = typeof as === "string";
  if (isElementTag) {
    let t2;
    if ($[7] !== xstyle) {
      t2 = xstyle && stylex.props(xstyle);
      $[7] = xstyle;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== Component || $[10] !== resolvedContent || $[11] !== restProps || $[12] !== t2) {
      t3 = /*#__PURE__*/_jsx(Component, Object.assign({}, restProps, t2, {
        children: resolvedContent
      }));
      $[9] = Component;
      $[10] = resolvedContent;
      $[11] = restProps;
      $[12] = t2;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  } else {
    let t2;
    if ($[14] !== Component || $[15] !== resolvedContent || $[16] !== restProps || $[17] !== xstyle) {
      t2 = /*#__PURE__*/_jsx(Component, Object.assign({
        xstyle: xstyle
      }, restProps, {
        children: resolvedContent
      }));
      $[14] = Component;
      $[15] = resolvedContent;
      $[16] = restProps;
      $[17] = xstyle;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    return t2;
  }
};
SlotWrapper.displayName = 'SlotWrapper';